import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, UserPlus, Trash2, Clock } from "lucide-react";

const ROLE_CLS = {
  owner: "border-[#FF8800]/40 text-[#FF8800]",
  editor: "border-[#00B0FF]/40 text-[#00B0FF]",
  viewer: "border-slate-500/40 text-slate-400",
};

export function TeamPanel({ startup, testid = "team-panel" }) {
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [sending, setSending] = useState(false);
  const isOwner = (startup.member_role || "owner") === "owner";

  const load = useCallback(() => {
    api.get(`/portal/startups/${startup.id}/team`).then((r) => setTeam(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [startup.id]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post(`/portal/startups/${startup.id}/team/invite`, { email, role });
      toast.success(res.data.registered ? "Invitation sent — they'll see it on their dashboard" : "Invitation recorded — it activates once they register with this email");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove ${label}?`)) return;
    try {
      await api.delete(`/portal/startups/${startup.id}/team/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!team) {
    return <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid={testid}>
      {isOwner && (
        <form onSubmit={invite} className="glass rounded-xl p-5" data-testid={`${testid}-invite-form`}>
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-4 flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5" /> Invite co-founder / team member
          </p>
          <div className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs">Email *</Label>
              <Input data-testid={`${testid}-invite-email`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cofounder@company.com" className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs">Access</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid={`${testid}-invite-role`} className="bg-[#06080E] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                  <SelectItem value="editor">Editor — can edit answers</SelectItem>
                  <SelectItem value="viewer">Viewer — read only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={sending} data-testid={`${testid}-invite-submit`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
              {sending ? "Sending..." : "Send Invite"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-3">Invited founders accept from their portal dashboard. Editors can update the questionnaire; viewers get read-only access. Only you can manage services, payments and the team.</p>
        </form>
      )}

      <div className="space-y-2.5" data-testid={`${testid}-list`}>
        <div className="glass rounded-xl px-4 py-3.5 flex items-center gap-3" data-testid={`team-member-${team.owner.id}`}>
          <div className="w-9 h-9 rounded-lg bg-[#FF8800]/10 border border-[#FF8800]/25 flex items-center justify-center shrink-0">
            <Crown className="w-4 h-4 text-[#FF8800]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{team.owner.name || "Owner"}</p>
            <p className="text-[11px] text-slate-500 truncate">{team.owner.email}</p>
          </div>
          <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${ROLE_CLS.owner}`}>OWNER</span>
        </div>

        {team.members.map((m) => (
          <div key={m.id} className="glass rounded-xl px-4 py-3.5 flex items-center gap-3" data-testid={`team-member-${m.id}`}>
            <div className="w-9 h-9 rounded-lg bg-[#00B0FF]/10 border border-[#00B0FF]/25 flex items-center justify-center shrink-0 text-[#00B0FF] font-display font-bold text-sm">
              {(m.name || m.email || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{m.name || m.email}</p>
              <p className="text-[11px] text-slate-500 truncate">{m.email}{m.joined_at ? ` · joined ${new Date(m.joined_at).toLocaleDateString("en-IN")}` : ""}</p>
            </div>
            <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${ROLE_CLS[m.role] || ROLE_CLS.viewer}`}>{m.role.toUpperCase()}</span>
            {isOwner && (
              <Button size="icon" variant="outline" data-testid={`team-remove-${m.id}`} onClick={() => remove(m.id, m.name || m.email)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}

        {team.pending_invites.map((i) => (
          <div key={i.id} className="glass rounded-xl px-4 py-3.5 flex items-center gap-3 opacity-80" data-testid={`team-invite-${i.id}`}>
            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-300 truncate">{i.email}</p>
              <p className="text-[11px] text-slate-500">Invitation pending · {i.role}</p>
            </div>
            <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-slate-500/40 text-slate-400">PENDING</span>
            {isOwner && (
              <Button size="icon" variant="outline" data-testid={`team-revoke-${i.id}`} onClick={() => remove(i.id, i.email)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}

        {team.members.length === 0 && team.pending_invites.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-6" data-testid={`${testid}-empty`}>Just you so far. Invite co-founders to collaborate on this startup.</p>
        )}
      </div>
    </div>
  );
}
